import { ColorByStatusPayment } from '@/enums/color-by-status-payment.enum'
import Button from '@components/buttons/button/button.component'
import StringsHelper from '@helpers/strings.helper'
import { HistoryTransaction } from '@interfaces/payment.interface'
import onLangChange from '@observables/on-lang-change.observable'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Subscription } from 'rxjs'
import ModalUnblockPayment from './components/modal-unblock-payment/modal-unblock-payment.component'
import PaymentStatus from './components/payment-status/payment-status.component'

import './card-payment-value.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  title?: any
  paymentObject: HistoryTransaction
  hideValue: boolean
  updateList?: () => any
  showIcon?: boolean
  onNavigateTo?: (path: string) => void
  disableTip?: boolean
}

export default ({ title = null, paymentObject, hideValue, updateList, showIcon = false, onNavigateTo, disableTip = false }: Props) => {
  const onLangChangeSubscription = useRef<Subscription | undefined>(undefined)
  const stringsHelper = useMemo(() => new StringsHelper(), [])
  const [t] = useTranslation('cardNextPayments')
  const [lang, setLang] = useState<string>('en')
  const [openModalUnblock, setOpenModalUnblock] = useState(false)
  const [width, setWidth] = useState(window.innerWidth)

  useEffect(() => {
    onLangChangeSubscription.current = onLangChange.subscribe((lang) => {
      setLang(lang)
    })

    return () => {
      onLangChangeSubscription.current?.unsubscribe()
    }
  }, [])

  useLayoutEffect(() => {
    function updateSize() {
      setWidth(window.innerWidth)
    }
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  const toPaymentDetails = useCallback(() => {
    if (onNavigateTo && typeof onNavigateTo === 'function') {
      onNavigateTo(`/payments/${paymentObject.transactionId}`)
    }
  }, [onNavigateTo, paymentObject.transactionId])

  const handleUpdateList = useCallback(() => {
    setOpenModalUnblock(false)
    if (updateList && typeof updateList === 'function') {
      updateList()
    }
  }, [updateList])

  const handleOpenModalUnblock = useCallback((e: Event) => {
    e?.stopPropagation()
    setOpenModalUnblock(true)
  }, [])

  const todayPlusMonth = useCallback(() => {
    const today = new Date().toISOString().split('T')[0]
    const newDate = new Date(today.substring(0, 7).concat('-02'))
    const datePlusMonth = new Date(newDate.setMonth(newDate.getMonth() + 1)).toISOString().split('T')[0]

    return `${stringsHelper.capitalizeFirstLetter(
      new Date(datePlusMonth)?.toLocaleString(lang, { month: 'long' })
    )} ${datePlusMonth?.substring(0, 4)}`
  }, [stringsHelper, lang])

  const PaymentAntecipation = () => {
    if (!paymentObject?.anticipated && !paymentObject?.canAnticipate) {
      return null
    }

    return (
      <div className="payment-antecipation">
        <i className="fa-solid fa-sack-dollar" style={{ color: 'var(--lilac)' }}></i>
        <p>{t(`${paymentObject?.anticipated ? 'anticipated' : 'canAnticipate'}`)}</p>
      </div>
    )
  }

  return (
    <section className="card-payment-value">
      <div className="card-button" role="button" tabIndex={0} onClick={() => toPaymentDetails()}>
        <div className="display-flex align-items-center">
          {showIcon && width > 575 ? (
            <div className="icon-status p-3 m-3 display-flex">
              <i
                className="fa-solid fa-sack-dollar"
                style={{ color: ColorByStatusPayment?.[paymentObject?.status], fontSize: '-webkit-xxx-large' }}
              ></i>
            </div>
          ) : null}
          <div className="content">
            <div className="info">
              <div>
                <span className="title">{title}</span>
                <PaymentStatus
                  status={paymentObject?.status}
                  paymentType={paymentObject?.paymentType}
                  hasNf={!!paymentObject?.nfId}
                  disableTip={disableTip}
                />
              </div>
              <span style={hideValue ? { fontSize: '2rem' } : {}}>
                {hideValue
                  ? '••••••'
                  : paymentObject?.netValue.toLocaleString('pt-br', {
                      style: 'currency',
                      currency: 'BRL',
                      maximumFractionDigits: 2,
                    })}
              </span>
            </div>
            {['blocked'].includes(paymentObject?.status) ? (
              <Button onClick={handleOpenModalUnblock} size="sm">
                <span>
                  <i className="fa-solid fa-unlock-keyhole" style={{ marginRight: '0.5rem' }}></i>
                  {t('unblock')}
                </span>
              </Button>
            ) : null}
          </div>
        </div>

        <PaymentAntecipation />
      </div>

      <ModalUnblockPayment
        openModal={openModalUnblock}
        closeModal={() => handleUpdateList()}
        transactionId={paymentObject?.transactionId}
        value={paymentObject?.netValue.toLocaleString('pt-br', {
          style: 'currency',
          currency: 'BRL',
          maximumFractionDigits: 2,
        })}
        date={todayPlusMonth()}
        paymentType={paymentObject?.paymentType}
      />
    </section>
  )
}
